import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9088/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear invalid token
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin');

      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin API
export const adminAPI = {
  login: (credentials) => api.post('/auth/admin/login', credentials),
  getStats: () => api.get('/admin/dashboard'),
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: () => api.get('/admin/analytics'),
  processExpiredSubscriptions: () => api.post('/admin/process-expired-subscriptions'),
};

// User Management API
export const userAPI = {
  getAll: (params) => api.get('/admin/users', { params }),
  getOne: (id) => api.get(`/admin/users/${id}`),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  updateSubscription: (id, data) => api.put(`/admin/users/${id}/subscription`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  suspend: (id) => api.post(`/admin/users/${id}/suspend`),
  unsuspend: (id) => api.post(`/admin/users/${id}/unsuspend`),
};

// Subscription Plans API
export const planAPI = {
  getAll: () => api.get('/admin/plans'),
  create: (data) => api.post('/admin/plans', data),
  update: (id, data) => api.put(`/admin/plans/${id}`, data),
  delete: (id) => api.delete(`/admin/plans/${id}`),
  updatePrice: (planId, billingPeriod, data) =>
    api.put(`/admin/plans/${planId}/prices/${billingPeriod}`, data),
  deletePrice: (planId, billingPeriod) =>
    api.delete(`/admin/plans/${planId}/prices/${billingPeriod}`),
};

// AI Models API
export const modelAPI = {
  getAll: () => api.get('/admin/models'),
  create: (data) => api.post('/admin/models', data),
  update: (id, data) => api.put(`/admin/models/${id}`, data),
  delete: (id) => api.delete(`/admin/models/${id}`),
  toggle: (id) => api.post(`/admin/models/${id}/toggle`),
  test: (id) => api.post(`/admin/models/${id}/test`),
};

// MCP Tools API
export const mcpAPI = {
  getAll: () => api.get('/admin/mcp'),
  create: (data) => api.post('/admin/mcp', data),
  update: (id, data) => api.put(`/admin/mcp/${id}`, data),
  delete: (id) => api.delete(`/admin/mcp/${id}`),
  toggle: (id) => api.post(`/admin/mcp/${id}/toggle`),
  updateToken: (id, api_token) => api.patch(`/admin/mcp/${id}/token`, { api_token }),
  test: (id, params) => api.post(`/admin/mcp/${id}/test`, { params }),
};

// Templates API
export const templateAPI = {
  getAll: () => api.get('/admin/templates'),
  create: (data) => api.post('/admin/templates', data),
  update: (id, data) => api.put(`/admin/templates/${id}`, data),
  delete: (id) => api.delete(`/admin/templates/${id}`),
};

// Logs API
export const logAPI = {
  getAll: (params) => api.get('/admin/logs', { params }),
};

// Configuration API
export const configAPI = {
  getAIAvatarConfig: () => api.get('/admin/config/ai-avatar'),
  saveAIAvatarConfig: (data) => api.post('/admin/config/ai-avatar', data),
};

// Blog API
export const blogAPI = {
  getAllPosts: (params) => api.get('/blog/admin/all', { params }),
  createPost: (data) => api.post('/blog/admin/create', data),
  updatePost: (id, data) => api.put(`/blog/admin/${id}`, data),
  deletePost: (id) => api.delete(`/blog/admin/${id}`),
  publishPost: (id) => api.patch(`/blog/admin/${id}/publish`, {}),
  unpublishPost: (id) => api.patch(`/blog/admin/${id}/unpublish`, {}),
  deleteComment: (id) => api.delete(`/blog/admin/comments/${id}`),
  getComments: (postId) => api.get(`/blog/${postId}/comments`),
  getPublishedPosts: (params) => api.get('/blog/', { params }),

  // Tag management
  getAllTags: () => api.get('/blog/tags'),
  createTag: (data) => api.post('/blog/admin/tags', data),
  updateTag: (id, data) => api.put(`/blog/admin/tags/${id}`, data),
  deleteTag: (id) => api.delete(`/blog/admin/tags/${id}`),
  addTagsToPost: (postId, tagIds) => api.post(`/blog/admin/${postId}/tags`, { tagIds }),
  getPostTags: (postId) => api.get(`/blog/${postId}/tags`),
};

// Content Generation API (for AI blog creation)
export const contentAPI = {
  generateBlogPost: (data) => api.post('/content/blog-post/generate', data, { timeout: 1800000 }), // 30 min timeout
};

// Billing Management API (Admin views user billing)
export const billingAPI = {
  getUserRecords: (userId, params) => api.get(`/admin/users/${userId}/billing/records`, { params }),
  getUserInvoices: (userId, params) => api.get(`/admin/users/${userId}/billing/invoices`, { params }),
  getUserStats: (userId) => api.get(`/admin/users/${userId}/billing/stats`),
  updateInvoiceStatus: (invoiceId, data) => api.put(`/admin/billing/invoices/${invoiceId}/status`, data),
  uploadInvoicePDF: (invoiceId, file) => {
    const formData = new FormData();
    formData.append('invoice', file);
    return api.post(`/admin/billing/invoices/${invoiceId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  exportUserReport: (userId) => {
    return api.get(`/admin/users/${userId}/billing/export`, {
      responseType: 'blob' // Important for file download
    });
  },
  exportAllUsersReport: () => {
    return api.get('/admin/billing/export-all', {
      responseType: 'blob' // Important for file download
    });
  }
};

export default api;
